import type {
  ConfigUniqueId,
  FilterableAttribute,
  FilterableValue
} from '@/domain/FilteringAndSorting';
import type { Product, ProductTechnology, ProductType } from '@/domain/Product';

export function getFilterableAttributeValuesForAutomatic(
  filterableAttributeValues: Map<FilterableValue, FilterableAttribute>,
  filterConfigId: ConfigUniqueId,
  products: Product[]
): Map<FilterableValue, FilterableAttribute> {
  const filterableAttributeValuesForAutomatic = new Map(
    filterableAttributeValues
  );

  if (filterConfigId === 'collections') {
    for (const [value] of filterableAttributeValuesForAutomatic.entries()) {
      const productsInCollection = products.filter(
        (product) =>
          Array.isArray(product.specifications.collections) &&
          product.specifications.collections.some(
            (collection) => collection.value === value
          )
      );

      const allAreAccessories = productsInCollection.every(
        (product) => product.type === 'accessory'
      );

      if (allAreAccessories) {
        filterableAttributeValuesForAutomatic.delete(value);
      }
    }
  }

  return filterableAttributeValuesForAutomatic;
}

export function cleanProductsOfCollection(
  productsOfCollectionToClean: Product[],
  technology: string,
  productType: ProductType
): Product[] | null {
  if (!productsOfCollectionToClean || productsOfCollectionToClean.length === 0)
    return null;

  // https://nestle.atlassian.net/browse/ITNP-152620 AC4
  // if no coffees in the collection and we are in capsules, don't show the collection
  if (
    (productType === 'capsule' || productType === 'machine') &&
    productsOfCollectionToClean.every((product) => product.type === 'accessory')
  ) {
    return null;
  }

  const accessoryWithMultipleTechnologiesInCollectionExists =
    productsOfCollectionToClean.some(
      (product) =>
        product.type === 'accessory' && product.technologies?.length > 1
    );

  // all ACs of https://nestle.atlassian.net/browse/ITNP-152620 refer to accessories
  // with multiple technologies, so if we don't have them, we can return the original collection
  if (!accessoryWithMultipleTechnologiesInCollectionExists) {
    return productsOfCollectionToClean;
  }

  let filteredCollections: Product[] = productsOfCollectionToClean;

  const collectionHasCapsulesOfTech = (
    productTechnology: ProductTechnology,
    collection: Product[]
  ) =>
    collection.some((product) => {
      const output =
        product.type === 'capsule' &&
        product.technologies?.includes(productTechnology);

      return output;
    });

  const filterAccessoriesOfTech = (
    collection: Product[],
    productTechnology: ProductTechnology
  ) =>
    collection.filter(
      (product) =>
        product.type === 'accessory' ||
        product.technologies?.includes(productTechnology)
    );

  // eslint-disable-next-line default-case
  switch (technology) {
    case 'original': {
      const hasCollectionHasCapsulesOfTech = collectionHasCapsulesOfTech(
        'original',
        productsOfCollectionToClean
      );

      if (!hasCollectionHasCapsulesOfTech) {
        filteredCollections = filterAccessoriesOfTech(
          productsOfCollectionToClean,
          'vertuo'
        );
      }

      break;
    }

    case 'vertuo': {
      const hasCollectionHasCapsulesOfTech = collectionHasCapsulesOfTech(
        'vertuo',
        productsOfCollectionToClean
      );

      if (!hasCollectionHasCapsulesOfTech) {
        filteredCollections = filterAccessoriesOfTech(
          productsOfCollectionToClean,
          'vertuo'
        );
      }

      break;
    }

    case 'pro': {
      const hasCollectionHasCapsulesOfTech = collectionHasCapsulesOfTech(
        'pro',
        productsOfCollectionToClean
      );

      if (!hasCollectionHasCapsulesOfTech) {
        filteredCollections = filterAccessoriesOfTech(
          productsOfCollectionToClean,
          'pro'
        );
      }

      break;
    }
  }

  if (filteredCollections.length === 0) {
    return null;
  }

  return filteredCollections;
}
